import React from "react";
import { connect } from "react-redux";

import Button from "components/CustomButtons/Button.jsx";
import { setAppMessage } from "../../app-actions.js";
import { MessageTypes } from "../../app-constants/index.js";
import ReviewDialog from "./components/review-dialog.jsx";
import {
  requestUploadUrl,
  putFileToR2,
  startAnalyze,
  getAnalysis,
  commit,
  cancelStaging
} from "./media-library-api.js";

// Media Library v2 — verify-before-save upload flow (old CMS).
//
//   SELECT     pick a file
//   UPLOADING  presign → PUT to R2 → analyze → poll until analysis is ready
//   REVIEW     edit metadata, resolve duplicates, Save (commit) or Cancel
//   COMPLETE   committed; nothing is written until Save
//
// Everything hits the absinthe proxy (REACT_APP_API_SERVER); the large-file PUT
// alone goes straight to R2. `domainId` (the session's activeDomain) rides every
// proxied call so absinthe can verify ownership and resolve the brand.

const PHASE = {
  SELECT: "SELECT",
  UPLOADING: "UPLOADING",
  REVIEW: "REVIEW",
  COMPLETE: "COMPLETE"
};

const POLL_INTERVAL_MS = 800;

class MediaUploadContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      phase: PHASE.SELECT,
      file: null,
      stagingId: null,
      uploadPct: 0,
      statusText: "",
      analysis: null,
      saving: false,
      committed: null
    };
    this._pollTimer = null;
    this._unmounted = false;
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._pollTimer) clearTimeout(this._pollTimer);
  }

  error = message => {
    this.props.setAppMessage(message, { type: MessageTypes.ERROR });
  };

  onFileInput = e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = null;
    if (file) this.start(file);
  };

  start = file => {
    const domainId = this.props.activeDomain;
    const self = this;
    this.setState({
      phase: PHASE.UPLOADING,
      file: file,
      uploadPct: 0,
      statusText: "Requesting upload URL…",
      analysis: null,
      committed: null
    });

    requestUploadUrl(domainId, file)
      .then(function(res) {
        // { stagingId, presignedUrl, r2Key }
        self.setState({ stagingId: res.stagingId, statusText: "Uploading…" });
        return putFileToR2(res.presignedUrl, file, function(pct) {
          if (!self._unmounted) self.setState({ uploadPct: pct });
        }).then(function() {
          return res.stagingId;
        });
      })
      .then(function(stagingId) {
        self.setState({ statusText: "Analyzing…" });
        return startAnalyze(domainId, stagingId).then(function() {
          return stagingId;
        });
      })
      .then(function(stagingId) {
        self.poll(stagingId);
      })
      .catch(function(err) {
        if (self._unmounted) return;
        self.error((err && err.message) || "Upload failed.");
        self.setState({ phase: PHASE.SELECT });
      });
  };

  poll = stagingId => {
    const domainId = this.props.activeDomain;
    const self = this;
    getAnalysis(domainId, stagingId)
      .then(function(analysis) {
        if (self._unmounted) return;
        if (analysis.status === "analyzing") {
          self._pollTimer = setTimeout(function() {
            self.poll(stagingId);
          }, POLL_INTERVAL_MS);
          return;
        }
        if (analysis.status === "error" || analysis.error) {
          self.error(analysis.error || "Analysis failed.");
          self.setState({ phase: PHASE.SELECT });
          return;
        }
        // Any non-"analyzing" status → ready to review.
        self.setState({ phase: PHASE.REVIEW, analysis: analysis });
      })
      .catch(function(err) {
        if (self._unmounted) return;
        self.error((err && err.message) || "Analysis failed.");
        self.setState({ phase: PHASE.SELECT });
      });
  };

  handleSave = payload => {
    const domainId = this.props.activeDomain;
    const stagingId = this.state.stagingId;
    const self = this;
    const body = Object.assign({ stagingId: stagingId }, payload);
    this.setState({ saving: true });
    return commit(domainId, body)
      .then(function(res) {
        if (self._unmounted) return res;
        self.setState({ phase: PHASE.COMPLETE, saving: false, committed: res });
        self.props.setAppMessage("Saved to library.", {
          type: MessageTypes.SUCCESS
        });
        if (self.props.onComplete) self.props.onComplete(res);
        return res;
      })
      .catch(function(err) {
        if (self._unmounted) return Promise.reject(err);
        self.setState({ saving: false });
        if (err && err.mandatoryMissing) {
          self.error("Please fill: " + err.mandatoryMissing.join(", "));
        } else {
          self.error((err && err.message) || "Save failed.");
        }
        // Re-throw so the dialog can highlight the missing fields.
        return Promise.reject(err);
      });
  };

  handleCancel = () => {
    const domainId = this.props.activeDomain;
    const stagingId = this.state.stagingId;
    if (stagingId) {
      // Fire-and-forget staging cleanup; nothing was committed.
      cancelStaging(domainId, stagingId).catch(function() {});
    }
    if (this._pollTimer) clearTimeout(this._pollTimer);
    this.reset();
    if (this.props.onCancel) this.props.onCancel();
  };

  reset = () => {
    this.setState({
      phase: PHASE.SELECT,
      file: null,
      stagingId: null,
      uploadPct: 0,
      statusText: "",
      analysis: null,
      saving: false,
      committed: null
    });
  };

  render() {
    const phase = this.state.phase;

    return (
      <div>
        {phase === PHASE.SELECT ? (
          <div>
            <input
              id="media-library-file-input"
              type="file"
              accept="image/*,video/*,audio/*"
              style={{ display: "none" }}
              onChange={this.onFileInput}
            />
            <Button
              color="rose"
              onClick={function() {
                const el = document.getElementById("media-library-file-input");
                if (el) el.click();
              }}
            >
              Upload media
            </Button>
          </div>
        ) : null}

        {phase === PHASE.UPLOADING ? (
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 8 }}>{this.state.statusText}</div>
            <div
              style={{
                background: "#eee",
                borderRadius: 4,
                height: 8,
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  background: "#e91e63",
                  height: "100%",
                  width: this.state.uploadPct + "%",
                  transition: "width 0.2s"
                }}
              />
            </div>
          </div>
        ) : null}

        {phase === PHASE.REVIEW && this.state.analysis ? (
          <ReviewDialog
            open={true}
            analysis={this.state.analysis}
            saving={this.state.saving}
            onSave={this.handleSave}
            onCancel={this.handleCancel}
          />
        ) : null}

        {phase === PHASE.COMPLETE ? (
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 12 }}>
              Saved
              {this.state.committed && this.state.committed.publicId
                ? " — " + this.state.committed.publicId
                : ""}
              .
            </div>
            <Button color="rose" onClick={this.reset}>
              Upload another
            </Button>
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = store => {
  const { activeDomain } = store.login.user;
  return { activeDomain: activeDomain };
};

const mapDispatchToProps = dispatch => ({
  setAppMessage: function(message, params) {
    return dispatch(setAppMessage(message, params));
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MediaUploadContainer);
